import {useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {syncMatchRedirect, EvaluatedRedirect} from '@epic-mw/redirects';
import {generateRouteHref} from '../../utils/route';

interface Props {
  children: JSX.Element;
  redirects: EvaluatedRedirect[];
  locale: string;
  isEnabled: boolean;
}

export function recursiveMatchRedirect(
  path: string,
  redirects: EvaluatedRedirect[]
): EvaluatedRedirect | undefined {
  const redirectTo = syncMatchRedirect(path, redirects);

  if (!redirectTo) {
    return undefined;
  }

  const next = recursiveMatchRedirect(redirectTo.to, redirects);
  if (next) {
    return next;
  }

  return redirectTo;
}

export function buildTargetHref(
  redirect: EvaluatedRedirect,
  currentSearchParams: URLSearchParams,
  locale: string
): string {
  const {to: href, preserveQueryString = false} = redirect;
  if (href.includes('http')) {
    if (!preserveQueryString) {
      return href;
    }

    try {
      const url = new URL(href);
      const queries = new URLSearchParams(currentSearchParams);
      // copy and override if necessary
      url.searchParams.forEach((value, key) => queries.set(key, value));
      // update URL to new set of queries
      url.search = queries.toString();
      return url.toString();
    } catch (err) {
      console.log(`failed to parse ${href} as URL`);
      throw err;
    }
  } else {
    if (!preserveQueryString && href.includes('?')) {
      const [path, searchAndHash] = href.split('?');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_search, hash] = searchAndHash.split('#');

      return generateRouteHref(`${path}${hash ? `#${hash}` : ''}`, currentSearchParams, locale);
    }

    // otherwise we assume this is a pathname with possibly query string and/or hash
    return generateRouteHref(href, currentSearchParams, locale);
  }
}

export function RedirectsManager({children, redirects, locale, isEnabled}: Props): JSX.Element {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (isEnabled) {
      const redirect = recursiveMatchRedirect(location.pathname, redirects);
      if (redirect) {
        const targetHref = buildTargetHref(redirect, new URLSearchParams(location.search), locale);
        if (targetHref.startsWith('http')) {
          window.location.replace(targetHref);
        } else {
          history.replace(targetHref);
        }
      }
    }
  }, [location.pathname, redirects]);

  return children;
}
